'use client';
import React, { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    course: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    // Simulate submission
    await new Promise((r) => setTimeout(r, 1000));
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <section id="talk-to-us" className="relative scroll-mt-24 bg-cream py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 items-stretch gap-8 lg:grid-cols-12 lg:gap-10">
          {/* Left: Contact Info */}
          <div className="h-full lg:col-span-5">
            <div className="flex h-full w-full flex-col gap-8 rounded-3xl border border-forest/10 bg-white/80 p-6 text-left shadow-lg backdrop-blur-md md:p-8">
              <div>
                <span className="text-saffron-dark uppercase font-bold tracking-widest text-xs md:text-sm border-b-2 border-saffron-light pb-1">
                  Get In Touch
                </span>
                <h2 className="font-serif text-3xl md:text-4xl text-forest font-bold mt-3 mb-4 leading-tight">
                  TALK TO US
                </h2>
                <p className="font-sans text-charcoal/70 text-sm md:text-base leading-relaxed">
                  Every meaningful journey begins with a conversation. Whether you&apos;re taking your very first teacher training or expanding your professional path, we&apos;re here to help you choose the program that best matches your aspirations.
                </p>
              </div>

              <div className="flex flex-col gap-5">
                {/* WhatsApp */}
                <a
                  href="https://wa.me/9779700193326"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-4 bg-white/60 hover:bg-white rounded-2xl border border-forest/5 hover:border-forest/10 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="bg-emerald-100 text-emerald-800 p-3 rounded-xl group-hover:scale-105 transition-transform">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-sans text-xs font-bold uppercase tracking-wider text-forest/70">WhatsApp Support</p>
                    <p className="font-sans text-sm font-semibold text-charcoal mt-0.5">+9779700193326</p>
                    <p className="text-[10px] text-emerald-600 font-medium mt-0.5">Active Now — Quick replies</p>
                  </div>
                </a>

                {/* Phone */}
                <a
                  href="tel:+9779700193326"
                  className="flex items-start gap-4 p-4 bg-white/60 hover:bg-white rounded-2xl border border-forest/5 hover:border-forest/10 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="bg-saffron-light/10 text-saffron-dark p-3 rounded-xl group-hover:scale-105 transition-transform">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-sans text-xs font-bold uppercase tracking-wider text-forest/70">Call Us Directly</p>
                    <p className="font-sans text-sm font-semibold text-charcoal mt-0.5">+977 9700193326</p>
                  </div>
                </a>

                {/* Email */}
                <a
                  href="mailto:YogaBowls.us@gmail.com"
                  className="flex items-start gap-4 p-4 bg-white/60 hover:bg-white rounded-2xl border border-forest/5 hover:border-forest/10 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="bg-forest/5 text-forest p-3 rounded-xl group-hover:scale-105 transition-transform">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-sans text-xs font-bold uppercase tracking-wider text-forest/70">Email Correspondence</p>
                    <p className="mt-0.5 break-all font-sans text-sm font-semibold text-charcoal">YogaBowls.us@gmail.com</p>
                  </div>
                </a>

                {/* Address */}
                <div className="flex items-start gap-4 p-4 bg-white/60 rounded-2xl border border-forest/5">
                  <div className="bg-charcoal/5 text-charcoal p-3 rounded-xl">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-sans text-xs font-bold uppercase tracking-wider text-forest/70">Main Academy Address</p>
                    <p className="font-sans text-sm font-semibold text-charcoal mt-0.5 leading-relaxed">Thamel, Kathmandu, Nepal</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="h-full lg:col-span-7">
            <div className="h-full w-full rounded-3xl border border-forest/10 bg-white/80 p-6 text-left shadow-lg backdrop-blur-md md:p-8">
              <h3 className="font-serif text-xl md:text-2xl text-forest font-bold mb-6">Send Us a Message</h3>

              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-600">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <path d="m9 11 3 3L22 4" />
                    </svg>
                  </div>
                  <h4 className="font-serif text-xl font-bold text-forest mb-2">Message Sent!</h4>
                  <p className="font-sans text-sm text-charcoal/70">Thank you for reaching out. We&apos;ll get back to you shortly via WhatsApp or email.</p>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="inquiry-name" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">
                      Your Full Name *
                    </label>
                    <input
                      id="inquiry-name"
                      name="name"
                      type="text"
                      required
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="inquiry-email" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">
                        Email Address *
                      </label>
                      <input
                        id="inquiry-email"
                        name="email"
                        type="email"
                        required
                        placeholder="name@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="inquiry-whatsapp" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">
                        WhatsApp Number *
                      </label>
                      <input
                        id="inquiry-whatsapp"
                        name="whatsapp"
                        type="text"
                        required
                        placeholder="+977-9800000000"
                        value={formData.whatsapp}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="inquiry-course" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">
                      Select Course of Interest
                    </label>
                    <select
                      id="inquiry-course"
                      name="course"
                      value={formData.course}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm"
                    >
                      <option value="">I&apos;m not sure / General Inquiry</option>
                      <option value="yoga-200">Yoga Teacher Training - 200 hrs [with free Singing Bowls basics]</option>
                      <option value="bowls-100">Buddha Bowls TTC - 100 hrs [with free Yoga basics]</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="inquiry-message" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">
                      Your Message *
                    </label>
                    <textarea
                      id="inquiry-message"
                      name="message"
                      required
                      rows={4}
                      placeholder="Tell us about your background or ask any questions..."
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-saffron-light to-saffron-dark text-white font-sans font-bold tracking-wider py-3.5 rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Sending...' : 'Submit Inquiry'}
                    {!submitting && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                        <path d="m21.854 2.147-10.94 10.939" />
                      </svg>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
